import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, terminate, clearIndexedDbPersistence } from 'firebase/firestore'
import { auth, db, googleProvider, appleProvider } from '@/lib/firebase'
import { identifyUser } from '@/lib/analytics'
import { fetchWebAccess } from '@/billing/access'
import type { WebUserSettings } from '@/types/settings'

/**
 * Web access state:
 *  - 'checking'  → still resolving (auth / settings / server verify in flight)
 *  - 'granted'   → Premium; the app is unlocked
 *  - 'denied'    → no active subscription; route to the gate
 */
export type AccessState = 'checking' | 'granted' | 'denied'

interface AuthContextValue {
  user: User | null
  uid: string | null
  userSettings: WebUserSettings | null
  /** Signed in, settings load finished, and no userSettings doc exists (never opened the app). */
  profileMissing: boolean
  /** userSettings exists but onboarding never completed (social sign-in that bailed out of the app's onboarding). */
  needsOnboarding: boolean
  loading: boolean
  settingsLoading: boolean
  isPremium: boolean
  access: AccessState
  recheckAccess: () => Promise<void>
  signInEmail: (email: string, password: string) => Promise<void>
  signInGoogle: () => Promise<void>
  signInApple: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userSettings, setUserSettings] = useState<WebUserSettings | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [settingsLoading, setSettingsLoading] = useState(false)
  // Set to the uid once the settings subscription delivered a SERVER-confirmed
  // answer for that user — profileMissing must never come from a cached miss.
  const [settingsResolvedUid, setSettingsResolvedUid] = useState<string | null>(null)
  const [serverAccess, setServerAccess] = useState<boolean | null>(null)
  const [accessChecking, setAccessChecking] = useState(false)
  const [accessChecked, setAccessChecked] = useState(false)

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u)
      identifyUser(u ? u.uid : null)
      setAuthLoading(false)
      if (!u) {
        setUserSettings(null)
        setServerAccess(null)
        setSettingsResolvedUid(null)
      }
    })
  }, [])

  // Real-time userSettings (mirrors mobile UserSettingsProvider). Path-based
  // rules allow reads of a not-yet-created doc, so a miss is a real miss.
  useEffect(() => {
    if (!user) return
    const uid = user.uid
    setSettingsLoading(true)
    setSettingsResolvedUid(null)
    const unsub = onSnapshot(
      doc(db, 'userSettings', uid),
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.exists()) {
          setUserSettings({ ...(snap.data() as WebUserSettings), uid })
          setSettingsResolvedUid(uid)
        } else if (!snap.metadata.fromCache) {
          setUserSettings(null)
          setSettingsResolvedUid(uid)
        }
        setSettingsLoading(false)
      },
      (err) => {
        console.error('userSettings subscription failed:', err)
        setSettingsLoading(false)
      },
    )
    return unsub
  }, [user])

  // Server verify (once per auth session) — flips a stale mirror to true after a fresh phone purchase.
  useEffect(() => {
    if (!user) return
    let cancelled = false
    setAccessChecking(true)
    setAccessChecked(false)
    fetchWebAccess()
      .then((ok) => {
        if (!cancelled) setServerAccess(ok)
      })
      .catch(() => {
        if (!cancelled) setServerAccess(null)
      })
      .finally(() => {
        if (!cancelled) {
          setAccessChecking(false)
          setAccessChecked(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const recheckAccess = useCallback(async () => {
    if (!auth.currentUser) return
    setAccessChecking(true)
    try {
      setServerAccess(await fetchWebAccess())
    } catch {
      /* keep previous */
    } finally {
      setAccessChecking(false)
    }
  }, [])

  const profileMissing = !!user && settingsResolvedUid === user.uid && userSettings === null
  const needsOnboarding = !!userSettings && (userSettings.onboard === false || (userSettings.onboard === undefined && !userSettings.profile))
  const hasMirror = userSettings?.premium === true

  useEffect(() => {
    if (user) identifyUser(user.uid, hasMirror)
  }, [user, hasMirror])

  const devBypass = import.meta.env.DEV && import.meta.env.VITE_BYPASS_GATE === '1'

  // The mirror (userSettings.premium) is what the mobile app gates on, so it
  // is authoritative here too; the server verify can only add access.
  let access: AccessState
  if (devBypass) access = 'granted'
  else if (hasMirror) access = 'granted'
  else if (serverAccess === true) access = 'granted'
  else if (!accessChecked || accessChecking || settingsLoading) access = 'checking'
  else access = 'denied'

  const value: AuthContextValue = {
    user,
    uid: user?.uid ?? null,
    userSettings,
    profileMissing,
    needsOnboarding,
    loading: authLoading,
    settingsLoading,
    isPremium: hasMirror || serverAccess === true,
    access,
    recheckAccess,
    signInEmail: async (email, password) => {
      await signInWithEmailAndPassword(auth, email, password)
    },
    signInGoogle: async () => {
      await signInWithPopup(auth, googleProvider)
    },
    signInApple: async () => {
      await signInWithPopup(auth, appleProvider)
    },
    resetPassword: async (email) => {
      await sendPasswordResetEmail(auth, email)
    },
    signOut: async () => {
      // Per-user caches (coach note, AI insights, coach activation) must not survive on a shared computer.
      const uidNow = auth.currentUser?.uid
      try {
        const keys: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          if (k && (k.startsWith('@vore_coach_note_') || k.startsWith('@vore_ai_insights_') || k.startsWith('@vore_coach_enabled') || (uidNow && k.endsWith(uidNow)))) keys.push(k)
        }
        keys.forEach((k) => localStorage.removeItem(k))
      } catch {
        /* storage unavailable */
      }
      await fbSignOut(auth)
      // Shared-computer hygiene: the persistent cache holds health data on disk.
      try {
        await terminate(db)
        await clearIndexedDbPersistence(db)
      } catch {
        /* multi-tab or already terminated */
      }
      window.location.replace('/sign-in')
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/** Mobile `useUserSettings()` equivalent. */
export function useUserSettings() {
  const { userSettings, isPremium, uid } = useAuth()
  return { userSettings, premium: isPremium, uid, units: userSettings?.units, profile: userSettings?.profile }
}
